import _ from "lodash";

import cloud from "@/api/cloud";
import { set } from "@/utils/vuex";

import { createCommunity, createUser } from "../model";

const getInitState = () => ({
	groups: [],
	communities: [],
	users: [],

	selected: {
		groupId: null,
		communityIds: [],
		userIds: [],
	},
});
export default {
	namespaced: true,

	state: getInitState(),

	getters: {
		// GROUP
		// -----
		groups(state) {
			return _.sortBy(state.groups, group => _.toLower(group.name));
		},

		selectedGroupId(state) {
			return state.selected.groupId;
		},

		selectedGroup(state, getters) {
			return _.find(state.groups, ["id", getters.selectedGroupId]);
		},

		// COMMUNITY
		// ---------
		communities(state) {
			return _.chain(state.communities)
				.map(createCommunity)
				.sortBy(community => _.toLower(community.name))
				.value();
		},

		selectedCommunityIds: state => state.selected.communityIds,

		selectedCommunities(state, getters) {
			return _.map(getters.selectedCommunityIds, communityId =>
				_.find(state.communities, ["id", communityId])
			);
		},

		// USER
		// ----
		users(state) {
			return _.chain(state.users)
				.map(createUser)
				.sortBy(user => _.toLower(user.displayName || user.email))
				.value();
		},

		selectedUserIds: state => state.selected.userIds,

		selectedUsers(state, getters) {
			return _.map(getters.selectedUserIds, userId =>
				_.find(state.users, ["guid", userId])
			);
		},
	},

	mutations: {
		// GROUP
		// -----
		setGroups: set("groups"),
		setSelectedGroupId(state, groupId) {
			state.selected = { ...state.selected, groupId };
		},

		// COMMUNITY
		// ---------
		setCommunities: set("communities"),
		setSelectedCommunityIds(state, communityIds) {
			state.selected = { ...state.selected, communityIds };
		},
		resetCommunities(state) {
			state.communities = [];
			state.selected = { ...state.selected, communityIds: [] };
		},

		// USER
		// ----
		setUsers: set("users"),
		setSelectedUserIds(state, userIds) {
			state.selected = { ...state.selected, userIds };
		},
		resetUsers(state) {
			state.users = [];
			state.selected = { ...state.selected, userIds: [] };
		},

		// ----------

		resetState(state) {
			Object.assign(state, getInitState());
		},
	},

	actions: {
		// GROUP
		// -----
		async getGroups({ commit }) {
			const accessGroups = await cloud.getAccessGroups();
			commit("setGroups", accessGroups);
		},

		async selectGroup({ commit, dispatch }, groupId) {
			commit("resetCommunities");
			commit("resetUsers");
			commit("setSelectedGroupId", groupId);

			!_.isNil(groupId) &&
				(await Promise.all([dispatch("getCommunities"), dispatch("getUsers")]));
		},

		async addGroup({ dispatch, getters }, payload) {
			const existing = _.find(getters.groups, ["name", payload.name]);
			if (existing) {
				throw new Error("A group with the same name already exists");
			}
			await cloud.createAccessGroup(payload.name);

			// Refresh group list
			await dispatch("getGroups");
		},

		async renameGroup({ state, getters, dispatch }, payload) {
			const groupId = state.selected.groupId;
			const accessGroup = _.find(state.groups, ["id", groupId]);
			const newName = payload.name;

			if (newName === accessGroup.name) {
				// Name is identical. Skip.
				return;
			}

			const existing = _.find(getters.groups, ["name", payload.name]);
			if (existing) {
				throw new Error("A group with the same name already exists");
			}

			await cloud.updateAssociation(groupId, { name: newName });

			// Refresh group list
			await dispatch("getGroups");
		},

		// COMMUNITIES
		// -----------
		async getCommunities({ state, commit }) {
			const groupId = state.selected.groupId;
			const resp = await cloud.getAccessGroupCommunities(groupId);
			commit("resetCommunities");
			commit("setCommunities", resp);
		},

		selectCommunities({ commit }, communityIds) {
			commit("setSelectedCommunityIds", communityIds);
		},

		async addCommunitiesToGroup({ state, dispatch }, payload) {
			const groupId = state.selected.groupId;
			const promises = [];
			_.forEach(payload.communities, communityId => {
				promises.push(cloud.addAccessGroupAssociation(groupId, communityId));
			});
			await Promise.all(promises);

			// Refresh communities list
			await dispatch("getCommunities");
		},

		async removeCommunitiesFromGroup({ state, dispatch }) {
			const groupId = state.selected.groupId;
			const communityIds = state.selected.communityIds;
			const promises = [];
			_.forEach(communityIds, communityId => {
				promises.push(cloud.removeAccessGroupAssociation(groupId, communityId));
			});
			await Promise.all(promises);

			// Refresh communities list
			await dispatch("getCommunities");
		},

		// USERS
		// -----
		async getUsers({ state, commit }, payload = {}) {
			const groupId = payload.groupId || state.selected.groupId;
			const resp = await cloud.getAssociationUsers(groupId);
			commit("resetUsers");
			commit("setUsers", resp);
		},

		selectUsers({ commit }, userIds) {
			commit("setSelectedUserIds", userIds);
		},

		async createUserInGroup({ dispatch, rootState }, payload) {
			// Create a brand new user and add to existing association group
			let user;
			try {
				user = await cloud.createUser({
					email: payload.email || null,
					password: payload.password,
				});
			} catch (err) {
				if (err.error === "user already exists") {
					user = await cloud.createUser({
						email: null,
						alternateEmail: payload.email,
						password: payload.password,
					});
				} else {
					throw err;
				}
			}

			// Add required properties
			await cloud.addUserProperty(user.guid, "firstName", payload.firstName);
			await cloud.addUserProperty(user.guid, "lastName", payload.lastName);

			// // Set to active
			await cloud.addUserProperty(user.guid, "status", "active");

			// Grant Ops, Installer and Advocate roles
			const opsRole = _.find(rootState.auth.roles, role => role.name === "Ops");
			await cloud.addUserRole(user.guid, opsRole.id);
			const installerRole = _.find(
				rootState.auth.roles,
				role => role.name === "Installer"
			);
			await cloud.addUserRole(user.guid, installerRole.id);
			if (payload.advocate) {
				const advocateRole = _.find(
					rootState.auth.roles,
					role => role.name === "Advocate"
				);
				await cloud.addUserRole(user.guid, advocateRole.id);
			}

			// Create association
			await dispatch("addUsersToGroup", {
				groupId: payload.associationId,
				users: [user.guid],
			});

			return user;
		},

		async addUsersToGroup({ state, dispatch }, payload) {
			const groupId = payload.groupId || state.selected.groupId;
			const promises = [];
			_.forEach(payload.users, userId => {
				promises.push(cloud.addAssociationUser(userId, groupId));
			});
			await Promise.all(promises);

			// Refresh users list
			await dispatch("getUsers", { groupId });
		},

		async removeUsersFromGroup({ state, dispatch }, payload) {
			const groupId = state.selected.groupId;
			const userIds = state.selected.userIds;
			const promises = [];
			_.forEach(userIds, userId => {
				promises.push(cloud.removeAssociationUser(userId, groupId));
			});
			await Promise.all(promises);

			// Refresh users list
			await dispatch("getUsers");
		},

		async userSearch(ctx, { associationId, search }) {
			const users = await cloud.getAssociationUsers(associationId, {
				search,
			});
			return _.chain(users)
				.map(createUser)
				.sortBy(user => _.toLower(user.displayName || user.email))
				.value();
		},
	},
};
